import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, PRESET_LABELS } from "@/lib/api";
import { confirmPaidRun, defaultOptions } from "@/components/engines/paid";

export const NewJobDialog = ({ open, onOpenChange, media, mediaIds, onCreated }) => {
  const [engines, setEngines] = useState([]);
  const [engine, setEngine] = useState("");
  const [preset, setPreset] = useState("DME");
  const [options, setOptions] = useState({});
  const [busy, setBusy] = useState(false);
  const batch = Array.isArray(mediaIds) && mediaIds.length > 0;

  useEffect(() => {
    if (!open) return;
    api.get("/engines").then((r) => {
      setEngines(r.data.engines);
      setEngine((e) => e || r.data.default);
    });
  }, [open]);

  const submit = async () => {
    if (!confirmPaidRun(engines, [engine], batch ? mediaIds.length : 1)) return;
    setBusy(true);
    try {
      if (batch) {
        const r = await api.post("/jobs/batch", { media_ids: mediaIds, engine, preset, engine_options: options });
        toast.success(`${r.data.created} jobs queued`);
      } else {
        await api.post("/jobs", { media_id: media.id, engine, preset, engine_options: options });
        toast.success("Separation job queued");
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create job");
    } finally {
      setBusy(false);
    }
  };

  const selected = engines.find((e) => e.key === engine);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-job-modal" className="bg-[#121721] border-[#253044] text-slate-100 max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">New separation job</DialogTitle>
          <DialogDescription className="text-slate-400 font-mono text-xs">
            {batch ? `${mediaIds.length} masters selected (batch)` : media?.source_filename}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="micro mb-1.5">Separation preset</div>
            <div className="grid grid-cols-1 gap-1">
              {Object.entries(PRESET_LABELS).map(([k, label]) => (
                <button key={k} data-testid={`stem-preset-${k.toLowerCase()}`} onClick={() => setPreset(k)}
                  className={`text-left px-3 py-2 rounded-sm border text-sm transition-colors ${preset === k ? "border-cyan-500 bg-cyan-500/10 text-white" : "border-[#253044] hover:bg-[#1E2738] text-slate-300"}`}>
                  <span className="font-mono text-xs mr-2 text-slate-500">{k}</span>{label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="micro mb-1.5">Separation engine</div>
            <select data-testid="engine-select-dropdown" value={engine} onChange={(e) => { setEngine(e.target.value); const eng = engines.find((x) => x.key === e.target.value); setOptions(eng ? defaultOptions(eng) : {}); }}
              className="w-full bg-[#0B0E14] border border-[#253044] rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-cyan-500">
              {engines.map((e) => (
                <option key={e.key} value={e.key}>{e.name}{e.paid ? " · PAID" : ""}{e.configured ? "" : " — not configured"}</option>
              ))}
            </select>
            {selected && (
              <div className="mt-2 text-xs text-slate-400 leading-relaxed">
                <div>{selected.description}</div>
                <div className={`mt-1 ${selected.configured ? "text-emerald-400" : "text-amber-400"}`}>
                  {selected.configured ? "Ready" : `Not configured — job will fail: ${selected.detail}`}
                </div>
                {selected.paid && <div data-testid="new-job-paid-warning" className="mt-1 text-amber-300">Paid engine — spends vendor credits; you will be asked to confirm.</div>}
                {Object.entries(selected.options || {}).map(([opt, schema]) => (
                  <label key={opt} className="block mt-2"><span className="micro">{schema.label}</span>
                    <select data-testid={`new-job-option-${opt}`} value={options[opt] ?? schema.default} onChange={(ev) => setOptions({ ...options, [opt]: ev.target.value })}
                      className="mt-1 w-full bg-[#0B0E14] border border-[#253044] rounded-sm px-2 py-1.5 text-xs text-slate-200">
                      {schema.choices.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select></label>
                ))}
              </div>
            )}
          </div>
          <div className="text-xs text-slate-500 font-mono">Output: WAV · 24-bit PCM · source sample rate preserved (48 kHz for lossy sources) · channels preserved</div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" data-testid="new-job-cancel-button" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="new-job-submit-button" disabled={busy || !engine} onClick={submit} className="bg-[#E11D48] hover:bg-[#be123c] text-white">
            {busy ? "Queuing…" : batch ? `Queue ${mediaIds.length} jobs` : "Queue job"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
